import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@dc-shared-web/components/shadcn/dialog'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { cn } from '@dc-shared-web/lib'
import { PropsWithChildren } from 'react'
import { faXmark } from '@document-crunch/pro-regular-svg-icons'
import { Icon } from '@dc-shared-web/components/base/Icon'

const dialogSizes = {
  small: 'tw-max-w-md',
  medium: 'tw-max-w-lg',
  large: 'tw-max-w-2xl',
}

export type BaseDialogProps = PropsWithChildren<{
  /** Title of the dialog */
  title: string
  /** Optional description of the dialog. If a string is provided, it will be displayed as a paragraph with some consisten styling. If a React node is provided, it will be displayed as is */
  description?: string | React.ReactNode
  /** Primary action slot of the dialog */
  primaryActionSlot: React.ReactNode
  /** Secondary action slot of the dialog. If not provided, a default secondary action slot will be displayed (Cancel button) */
  secondaryActionSlot?: React.ReactNode
  /** Tertiary action slot of the dialog */
  tertiaryActionSlot?: React.ReactNode
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback function to close the dialog. State must be handled by the parent component */
  onClose: () => void
  /** Size of the dialog */
  size?: 'small' | 'medium' | 'large'
  /** Test ID for the dialog content */
  'data-testid'?: string
  /** Whether to show the close button at the top right of the dialog */
  showCloseButton?: boolean
  /** Whether to disable closing the dialog when clicking outside */
  disableOutsideClick?: boolean
  /** Whether to disable closing the dialog when pressing the Escape key */
  disableEscapeClose?: boolean
}>

export const BaseDialog = ({
  title,
  description,
  children,
  primaryActionSlot,
  secondaryActionSlot,
  tertiaryActionSlot,
  isOpen,
  onClose,
  size = 'small',
  'data-testid': dataTestId,
  showCloseButton = false,
  disableOutsideClick = false,
  disableEscapeClose = false,
}: BaseDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'tw-gap-0 tw-p-6 tw-max-h-[90vh] tw-flex tw-flex-col',
          dialogSizes[size]
        )}
        data-testid={dataTestId}
        onPointerDownOutside={
          disableOutsideClick ? (e) => e.preventDefault() : undefined
        }
        onEscapeKeyDown={
          disableEscapeClose ? (e) => e.preventDefault() : undefined
        }
      >
        <DialogHeader className='tw-flex-shrink-0 tw-px-1'>
          <div className='tw-flex tw-flex-col'>
            <div className='tw-flex tw-items-center tw-justify-between'>
              <DialogTitle
                className='tw-font-semibold tw-text-lg'
                aria-label='Dialog title'
              >
                {title}
              </DialogTitle>
              {showCloseButton && (
                <Button variant='ghost' size='mini' onClick={onClose}>
                  <Icon icon={faXmark} />
                </Button>
              )}
            </div>
            {description &&
              (typeof description === 'string' ? (
                <DialogDescription className='tw-text-sm tw-text-neutral-300'>
                  {description}
                </DialogDescription>
              ) : (
                description
              ))}
          </div>
        </DialogHeader>
        <div className='tw-mt-2 tw-mb-3 tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-isolate tw-py-2 tw-px-1'>
          {children}
        </div>
        <DialogFooter className='tw-justify-between tw-flex-shrink-0 tw-px-1'>
          <div>{tertiaryActionSlot}</div>
          <div className='tw-flex tw-gap-2'>
            {secondaryActionSlot || (
              <Button
                variant='outline'
                onClick={onClose}
                data-testid='dialog-cancel-button'
              >
                Cancel
              </Button>
            )}
            {primaryActionSlot}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
